package org.example.project

import kotlinx.serialization.Serializable

@Serializable
enum class LicenseKeyStatus(val value: String) {
    ACTIVE("ACTIVE"),
    CLAIMED("CLAIMED"),
    REVOKED("REVOKED");

    companion object {
        fun fromString(value: String): LicenseKeyStatus {
            val upper = value.uppercase()
            return values().find { it.value == upper } ?: ACTIVE
        }
    }
}

@Serializable
data class LicenseKeyDocument(
    val key_id: String,

    val key_sn: String,
    val key_pin: String,

    val bonus_units: Int,
    val status: LicenseKeyStatus,

    val created_at: String, // Stored as String from Date mapping
    val claimed_at: String? = null,
    val revoked_at: String? = null,
    
    val creator_uid: String,
    val creator_email: String,
    val creator_role: UserRoleType = UserRoleType.ORGANIZATION,
    val claimer_uid: String? = null,
    val claimer_email: String? = null,
    val claimer_role: UserRoleType? = null,

    val create_ledger_id: String,
    val claim_ledger_id: String? = null,
    val revoke_ledger_id: String? = null
)

@Serializable
enum class UserRoleType(val value: String) {
    ADMIN("ADMIN"),
    ORGANIZATION("ORGANIZATION"),
    USER("USER");

    companion object {
        fun fromString(value: String): UserRoleType {
            val upper = value.uppercase()
            return entries.find { it.value == upper } ?: USER
        }
    }
}

@Serializable
enum class KYCType(val value: String) {
    NONE("NONE"),
    CORPORATE("CORPORATE");

    companion object {
        fun fromString(value: String): KYCType {
            val upper = value.uppercase()
            return entries.find { it.value == upper } ?: NONE
        }
    }
}

@Serializable
data class UserBalances(
    val iap_units: Int = 0,
    val bonus_units: Int = 0
)

@Serializable
data class UserMetadata(
    val usage_daily: Int = 0,       // 日消耗点数
    val usage_monthly: Int = 0,     // 月消耗点数
    val usage_total: Int = 0,       // 总消耗点数

    val usage_daily_reset_at: String = "",    // 上次日消耗重置时间
    val usage_monthly_reset_at: String = ""   // 上次月消耗重置时间
)

@Serializable
data class OrgMetadata(
    val level: Int = 1,
    val kyc_type: KYCType = KYCType.NONE,

    val provision_daily_limit_usd: Int = 0,
    val provision_monthly_limit_usd: Int = 0,

    val provision_daily_volume_usd: Int = 0,
    val provision_monthly_volume_usd: Int = 0,
    val provision_total_volume_usd: Int = 0,

    val provision_daily_last_reset_at: String = "",
    val provision_monthly_last_reset_at: String = "",

    val tax_country: String? = null,
    val tax_id: String? = null
)

@Serializable
// 系统内部配置文档结构 (数据库位置为 /system_profiles/{userId})
data class SystemProfileDocument(
    val uid: String = "",
    val role: UserRoleType = UserRoleType.USER,             // admin / org / user
    // 【机构相关元数据】
    val org_metadata: OrgMetadata? = null,
)

@Serializable
// 用户主文档结构 (数据库位置为 /users/{userId})
data class UserDocument(
    val uid: String = "",                    // 用户id
    val email: String = "",                  // 记录用户邮箱
    val display_name: String = "",           // masked_email 有时候会需要显示非敏感数据
    val balances: UserBalances = UserBalances(),             // 余额/资产
    val created_at: String = "",             // 用户创建时间

    // 【用户相关元数据】
    val user_metadata: UserMetadata = UserMetadata(),
)

@Serializable
// 渠道商等级配置表
data class OrgTierDocument(
    val level_id: Int,                           // 1/2/3/4
    val is_max_level: Boolean,                   // 是否为最高等级
    val provision_total_threshold_usd: Int,       // “达到当前这个等级”所需的门槛 (美元)
    val required_kyc_type: KYCType,              // 升级所需的认证类型
    val provision_daily_limit_usd: Int,           // 每日发码额度 (美元)
    val provision_monthly_limit_usd: Int          // 每月发码额度 (美元)
)

@Serializable
data class IapPackageDocument(
    val internal_id: String,
    val iap_units: Int,
    val apple_product_id: String
)

@Serializable
// 渠道进货包配置表
data class LicensePackageDocument(
    val package_id: String,                // pack名称
    val bonus_units: Int,          // pack 包含的 bonus_units
    val price_usd: Int,                 // 价格 (美元)
    val required_level: Int             // 购买所需的最低等级 (1/2/3)
)


@Serializable
data class UserCheckInfo(
    val role: UserRoleType,
    val uid_tail: String
)

sealed class UserCheckResult {
    data class Success(val data: UserCheckInfo) : UserCheckResult()
    data class Error(val message: String) : UserCheckResult()
}

sealed class GetOrgInfoResult {
    data class Success(val data: SystemProfileDocument) : GetOrgInfoResult()
    data class Error(val message: String) : GetOrgInfoResult()
}

@Serializable
data class TaxInfo(
    val tax_country: String?,
    val tax_id: String?
)

sealed class CheckTaxInfoResult {
    data class Success(val data: TaxInfo) : CheckTaxInfoResult()
    data class Error(val message: String) : CheckTaxInfoResult()
}

sealed class UpdateTaxInfoResult {
    object Success : UpdateTaxInfoResult()
    data class Error(val message: String) : UpdateTaxInfoResult()
}

sealed class EnableOrgResult {
    object Success : EnableOrgResult()
    data class Error(val message: String) : EnableOrgResult()
}

@Serializable
data class IpInfo(
    val ip: String
)

@Serializable
data class ApiResponse<T>(
    val code: Int,      // 冗余数据，一般情况不使用
    val message: String,
    val data: T? = null
)

// 定义一个专门给“只关心 message”接口用的类
@Serializable
data class SimpleResponse(
    val code: Int,      // 冗余数据，一般情况不使用
    val message: String,
)

@Serializable
data class LicenseKeyListResult(
    val data: List<LicenseKeyDocument>,
    val next_cursor: String? = null
)

sealed class GetIapPackagesResult {
    data class Success(val data: List<IapPackageDocument>) : GetIapPackagesResult()
    data class Error(val message: String) : GetIapPackagesResult()
}

sealed class LicensePackageListResult {
    data class Success(val data: List<LicensePackageDocument>) : LicensePackageListResult()
    data class Error(val message: String) : LicensePackageListResult()
}

sealed class OrgLevelListResult {
    data class Success(val data: List<OrgTierDocument>) : OrgLevelListResult()
    data class Error(val message: String) : OrgLevelListResult()
}

@Serializable
data class LicenseKeyPublicInfo(
    val key_sn: String,
    val bonus_units: Int,
    val status: LicenseKeyStatus
)

sealed class CheckLicenseKeyResult {
    data class Success(val data: LicenseKeyPublicInfo) : CheckLicenseKeyResult()
    data class Error(val message: String) : CheckLicenseKeyResult()
}

sealed class UseLicenseKeyResult {
    data class Success(val data: LicenseKeyPublicInfo) : UseLicenseKeyResult()
    data class Error(val message: String) : UseLicenseKeyResult()
}

sealed class RevokeLicenseKeyResult {
    object Success : RevokeLicenseKeyResult()
    data class Error(val message: String) : RevokeLicenseKeyResult()
}

sealed class OrgProvisionResult {
    object Success : OrgProvisionResult()
    data class Error(val message: String) : OrgProvisionResult()
}

sealed class OrgReclaimResult {
    object Success : OrgReclaimResult()
    data class Error(val message: String) : OrgReclaimResult()
}

sealed class CreateLicenseKeyResult {
    object Success : CreateLicenseKeyResult()
    data class Error(val message: String) : CreateLicenseKeyResult()
}

sealed class SyncWithBackendResult {
    object Success : SyncWithBackendResult()
    data class Error(val message: String) : SyncWithBackendResult()
}

sealed class GetTaxCountriesResult {
    data class Success(val data: List<String>) : GetTaxCountriesResult()
    data class Error(val message: String) : GetTaxCountriesResult()
}

sealed class GetFiatPaymentMethodsResult {
    data class Success(val data: List<String>) : GetFiatPaymentMethodsResult()
    data class Error(val message: String) : GetFiatPaymentMethodsResult()
}